import type {
  DeliveryProgress,
  FieldSets,
  OperatorEditsSummary,
} from '../types/run-detail.api.types';

/**
 * Whether the run's delivery push is confirmed: field sets' `deliveryConfirmed` when the backend
 * sends it (catalog-service §90), else `/progress` `delivery.status === 'DELIVERED'` with
 * something pushed (a suppressed delivery settles with nothing sent, so it never counts).
 */
export function isDeliveryConfirmed(
  fieldSets: FieldSets | null | undefined,
  delivery: DeliveryProgress | null | undefined,
): boolean {
  if (typeof fieldSets?.deliveryConfirmed === 'boolean') return fieldSets.deliveryConfirmed;
  return delivery?.status === 'DELIVERED' && !delivery.suppressed;
}

/**
 * True only when operator edits provably reached the destination: the push is confirmed AND
 * integrations reported the overlay (`operatorEdits` present, `unavailable: false`). An absent
 * report (older backend, or no callback yet) is never read as "delivered".
 */
export function areOperatorEditsDelivered(
  edits: OperatorEditsSummary | null | undefined,
  confirmed: boolean,
): boolean {
  return confirmed && !!edits && !edits.unavailable;
}

/**
 * Whether `excluded` can NOT be shown as a known count: integrations said a narrowed push couldn't
 * count (`NARROWED_UNKNOWN`), or — with no scope reported at all — the delivery was a retry (a
 * chain of attempts, or attempt > 1), which may have pushed only a slice. `RUN` and `NARROWED`
 * are exact. Shown as "—" when unknown.
 */
export function isExcludedCountUnknown(
  edits: OperatorEditsSummary,
  deliveryAttempt?: number | null,
): boolean {
  if (edits.excludedScope === 'RUN' || edits.excludedScope === 'NARROWED') return false;
  if (edits.excludedScope === 'NARROWED_UNKNOWN') return true;
  return (edits.attempts?.length ?? 0) > 1 || (deliveryAttempt ?? 1) > 1;
}
